import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { btLayoutSidebarActions, INLINE_SIDEBAR_COMPONENTS } from '@common-reducers/BtLayoutSidebarReducer';
import { Box, IconButton, Typography } from '@mui/material';
import { Close, OpenInNew } from '@mui/icons-material';
import { getSelectedBoardId } from '@common-reducers/BoardsSelectors';
import { appConfig } from "@common-config/app.config";
import NotesPanel from '@extension/context/content/components/notes-sidebar/notes-panel';
import { ExtensionImageProxyProvider } from '@extension/context/content/components/ExtensionImageProxyProvider';
import TasksSideBarWrapper from '@common-components/chat-messages/tasks-sidebar-wrapper';
import ChatScheduledMessages from '@common-views/whatsapp-view/ChatScheduledMessages';
import RemindersSidebar from '@common-views/whatsapp-view/RemindersSidebar';
import QuickAgentsSidebar from '@common-views/whatsapp-view/QuickAgentsSidebar';
import { selectCurrentWaChat } from './redux/TeamInboxSelectors';

const DEFAULT_WIDTH = 400;
const MIN_WIDTH = 300;
const MAX_WIDTH = 800;

export default function InlineSidebarTeamInbox() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { isOpen, componentToShow } = useAppSelector((state) => state.BtLayoutSidebarReducer);

    // Check if the selected component should render in the inline sidebar
    const isSidebarOpen = isOpen && INLINE_SIDEBAR_COMPONENTS.includes(componentToShow);
    const selectedBoardId = useAppSelector(getSelectedBoardId);

    // Get current chat name for title
    const currentChat = useAppSelector(selectCurrentWaChat);
    const chatName = currentChat?.name || '';

    // Generate sidebar title based on component and chat name
    const getSidebarTitle = () => {
        if (componentToShow === 'Reminders') {
            return t('teamInbox.reminders');
        }
        if (componentToShow === 'QuickAgents') {
            return t('teamInbox.quickAgents.sidebarTitle');
        }
        switch (componentToShow) {
            case 'ChatScheduledMessages':
                return chatName ? `${chatName} - ${t('teamInbox.scheduledMessages')}` : t('teamInbox.scheduledMessages');
            case 'Notes':
                return chatName ? `${chatName} - ${t('teamInbox.notes')}` : t('teamInbox.notes');
            case 'Tasks':
                return chatName ? `${chatName} - ${t('teamInbox.tasks')}` : t('teamInbox.tasks');
            default:
                return componentToShow;
        }
    };

    const sidebarRef = useRef<HTMLDivElement>(null);
    const isResizingRef = useRef(false);

    // Initialize width from local storage
    const [sidebarWidth, setSidebarWidth] = useState(() => {
        const saved = localStorage.getItem('bt-team-inbox-sidebar-width');
        return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
    });
    const sidebarWidthRef = useRef(sidebarWidth);

    useEffect(() => {
        sidebarWidthRef.current = sidebarWidth;
    }, [sidebarWidth]);

    const startResizing = React.useCallback((mouseDownEvent: React.MouseEvent) => {
        mouseDownEvent.preventDefault();
        isResizingRef.current = true;
        document.body.style.cursor = 'ew-resize';
    }, []);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizingRef.current) return;

            // Sidebar is on the right, so width is window width - mouse X
            let newWidth = window.innerWidth - e.clientX;

            if (newWidth < MIN_WIDTH) newWidth = MIN_WIDTH;
            if (newWidth > MAX_WIDTH) newWidth = MAX_WIDTH;

            setSidebarWidth(newWidth);
            sidebarWidthRef.current = newWidth;
        };

        const handleMouseUp = () => {
            if (isResizingRef.current) {
                isResizingRef.current = false;
                document.body.style.cursor = '';
                localStorage.setItem('bt-team-inbox-sidebar-width', String(sidebarWidthRef.current));
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
        };
    }, []);

    if (!isSidebarOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 48, // Below the top header
                right: 0,
                height: 'calc(100% - 48px)',
                width: `${sidebarWidth}px`,
                zIndex: 1300,
                backgroundColor: 'var(--wa-sidebar-bg, #fff)',
                display: 'flex',
                flexDirection: 'column',
                transition: isResizingRef.current ? 'none' : 'width 0.3s ease-out',
                borderInlineStart: '1px solid var(--wa-divider)',
                boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.1)',
            }}
            ref={sidebarRef}
        >
            {/* Drag Handle */}
            <div
                onMouseDown={startResizing}
                style={{
                    position: 'absolute',
                    left: -4,
                    top: 0,
                    bottom: 0,
                    width: '8px',
                    cursor: 'ew-resize',
                    zIndex: 10000,
                    backgroundColor: 'transparent',
                }}
            />

            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'var(--wa-panel-bg)', borderBottom: '1px solid var(--wa-divider)' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--wa-title-text)' }}>{getSidebarTitle()}</Typography>
                <Box>
                    {componentToShow === 'Tasks' && selectedBoardId && (
                        <IconButton
                            onClick={() => window.open(`${appConfig.APP_ENDPOINT}/tasks?boardID=${selectedBoardId}`, "_blank")}
                            title={t('teamInbox.openTasksInNewTab')}
                        >
                            <OpenInNew />
                        </IconButton>
                    )}
                    <IconButton onClick={() => dispatch(btLayoutSidebarActions.closeSidebar())}>
                        <Close />
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {componentToShow === 'Notes' && (
                    <ExtensionImageProxyProvider>
                        <NotesPanel />
                    </ExtensionImageProxyProvider>
                )}
                {componentToShow === 'Tasks' && (
                    <TasksSideBarWrapper hideHeader={true} />
                )}
                {componentToShow === 'ChatScheduledMessages' && (
                    <ExtensionImageProxyProvider>
                        <ChatScheduledMessages hideHeader={true} />
                    </ExtensionImageProxyProvider>
                )}
                {componentToShow === 'Reminders' && (
                    <ExtensionImageProxyProvider>
                        <RemindersSidebar />
                    </ExtensionImageProxyProvider>
                )}
                {componentToShow === 'QuickAgents' && (
                    <ExtensionImageProxyProvider>
                        <QuickAgentsSidebar />
                    </ExtensionImageProxyProvider>
                )}
            </Box>
        </div>
    );
}
